// Icon — the single icon surface for the app. Maps stable semantic names to
// lucide-react glyphs so callers write <Icon name="reports" /> and never import
// lucide directly. Swapping the underlying glyph for a name is a one-line change
// here. Renders inline SVG (CSP-safe, bundle-served) with consistent defaults.
import {
  LayoutDashboard, Briefcase, ListChecks, Map, Sprout, Users, FileText, Inbox,
  BarChart3, FileBarChart, Activity, MapPinned, Settings, UserCog, Landmark,
  Percent, FileSpreadsheet,
  PanelLeft, Menu, X, Check, RefreshCw, Search, Trash2, Download, Upload,
  Paperclip, StickyNote, Star, AlertTriangle, Folder, FolderOpen, File,
  ArrowLeftRight, ArrowLeft, ArrowRight, ArrowUp, ArrowDown, ArrowUpRight,
  Mic, Plus, Zap, List, Columns3, GanttChartSquare, Calendar, Sun, SquarePen,
  Stethoscope, Info, Printer, Filter, MapPin, BookText, Archive, Bell, User,
  ChevronDown, ChevronRight, ChevronUp, ChevronLeft, MoreHorizontal,
  MoreVertical, Eye, LogOut, CircleCheck, CircleAlert, Circle,
  Camera, Image as ImageIcon, PenLine, ImagePlus, Layers, Sprout as LandIcon,
  Play, Undo2, Maximize2, Minimize2, Maximize, Minimize, Lock, Sparkles,
  MessageSquare, Pin, TrendingUp, TrendingDown, LayoutGrid, Square, Hexagon,
  Ruler, Route, AtSign, Building2, Barcode,
  Send, Reply, ReplyAll, Forward, Mail as MailIcon, MailOpen, FolderPlus, Flag, Video, Clock,
  ThumbsUp, ThumbsDown, Link2,
  ScanText,
  Split,
  Utensils,
  Bird, Crosshair, Feather, Target, PartyPopper, BedDouble, Wine, HandHeart, Tent,
} from "lucide-react";

// Semantic name → lucide component. Keep names domain-oriented, not glyph-oriented.
const ICONS = {
  // ---- Navigation --------------------------------------------------------
  dashboard: LayoutDashboard,
  deals: Briefcase,
  tasks: ListChecks,
  map: Map,
  farms: Sprout,
  contacts: Users,
  documents: FileText,
  emailInbox: Inbox,
  reports: BarChart3,
  taskReport: FileBarChart,
  activityReport: Activity,
  parcelReport: MapPinned,
  realEstateReport: Building2,
  settings: Settings,
  users: UserCog,
  govwins: Landmark,
  rates: Percent,
  sage: FileSpreadsheet,

  // ---- Chrome / actions --------------------------------------------------
  sidebar: PanelLeft,
  menu: Menu,
  close: X,
  check: Check,
  refresh: RefreshCw,
  sync: RefreshCw,
  swap: ArrowLeftRight,
  search: Search,
  trash: Trash2,
  download: Download,
  upload: Upload,
  attach: Paperclip,
  mail: MailIcon,
  mailOpen: MailOpen,
  send: Send,
  reply: Reply,
  replyAll: ReplyAll,
  forward: Forward,
  newFolder: FolderPlus,
  flag: Flag,
  video: Video,
  clock: Clock,
  thumbsUp: ThumbsUp,
  thumbsDown: ThumbsDown,
  note: StickyNote,
  utensils: Utensils,
  gun: Crosshair,
  star: Star,
  warning: AlertTriangle,
  folder: Folder,
  folderOpen: FolderOpen,
  file: File,
  add: Plus,
  quickAdd: Zap,
  list: List,
  kanban: Columns3,
  gantt: GanttChartSquare,
  calendar: Calendar,
  sun: Sun,
  edit: SquarePen,
  diagnostics: Stethoscope,
  info: Info,
  print: Printer,
  filter: Filter,
  pin: MapPin,
  ledger: BookText,
  archive: Archive,
  bell: Bell,
  user: User,
  more: MoreHorizontal,
  moreVertical: MoreVertical,
  view: Eye,
  signOut: LogOut,
  external: ArrowUpRight,
  mic: Mic,
  camera: Camera,
  image: ImageIcon,
  barcode: Barcode,   // --- Retail products module (removable) ---
  imagePlus: ImagePlus,
  pen: PenLine,
  layers: Layers,
  land: LandIcon,
  play: Play,
  undo: Undo2,
  maximize: Maximize2,
  minimize: Minimize2,
  fullscreen: Maximize,
  fullscreenExit: Minimize,
  lock: Lock,
  scanText: ScanText,
  split: Split,
  sparkles: Sparkles,
  message: MessageSquare,
  mention: AtSign,
  pushpin: Pin,
  link: Link2,
  grid: LayoutGrid,
  circle: Circle,
  square: Square,
  area: Hexagon,
  ruler: Ruler,
  route: Route,
  stop: Square,
  record: Circle,

  // ---- Directional -------------------------------------------------------
  chevronDown: ChevronDown,
  chevronRight: ChevronRight,
  chevronUp: ChevronUp,
  chevronLeft: ChevronLeft,
  arrowLeft: ArrowLeft,
  arrowRight: ArrowRight,
  arrowUp: ArrowUp,
  arrowDown: ArrowDown,

  // ---- Status ------------------------------------------------------------
  success: CircleCheck,
  error: CircleAlert,
  trendUp: TrendingUp,
  trendDown: TrendingDown,

  // ---- Lodge booking choices ----------------------------------------------
  huntDuck: Bird,
  huntDeer: Crosshair,
  huntTurkey: Feather,
  huntUpland: Target,
  huntDove: Bird,
  privateEvent: PartyPopper,
  lodging: BedDouble,
  liquor: Wine,
  massage: HandHeart,
  camp: Tent,
};

// Icon — `name` selects the glyph; everything else mirrors lucide props.
// `size` (px), `color` (defaults to currentColor so it inherits text color),
// `strokeWidth`, plus `style`/`className`/`aria-*` pass through.
export const Icon = ({ name, size = 16, color = "currentColor", strokeWidth = 2, style, ...rest }) => {
  const Cmp = ICONS[name] || Circle;
  if (!ICONS[name] && process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn(`[Icon] unknown name "${name}" — falling back to a placeholder.`);
  }
  return (
    <Cmp
      size={size}
      color={color}
      strokeWidth={strokeWidth}
      aria-hidden={rest["aria-label"] ? undefined : true}
      style={{ flexShrink: 0, display: "block", ...style }}
      {...rest}
    />
  );
};

export default Icon;
